<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbs" />

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <div v-else>
      <!-- Header -->
      <div class="pi-header">
        <div class="pi-header-left">
          <div class="pi-header-icon" :class="providerDef.iconClass">
            <img
              v-if="providerDef.logo"
              :src="providerDef.logo"
              style="width: 26px; height: 26px;"
            />
            <i v-else :class="providerDef.icon" style="font-size: 26px;"></i>
          </div>
          <div>
            <div class="pi-header-title">{{ providerDef.name }}</div>
            <div class="pi-header-desc">{{ providerDef.description }}</div>
          </div>
        </div>
        <button class="di-btn di-btn-primary" @click="openNewWizard">
          <i class="bx bx-plus" style="font-size: 16px;"></i>
          Nova Integração
        </button>
      </div>

      <!-- Integration Cards -->
      <div v-if="providerIntegrations.length" class="pi-grid">
        <div
          v-for="item in providerIntegrations"
          :key="item.id"
          class="pi-card"
          :class="{
            active: item.is_active,
            inactive: !item.is_active
          }"
          @click="goToDetail(item)"
        >
          <div class="pi-card-top">
            <div class="pi-card-name">{{ item.name || 'Sem nome' }}</div>
            <span
              class="int-badge"
              :class="item.is_active ? 'active-badge' : 'inactive-badge'"
            >
              <template v-if="item.is_active">&#9679; Ativo</template>
              <template v-else>Desativado</template>
            </span>
          </div>

          <div class="pi-card-meta">
            <span v-if="item.sync_direction !== 'pull'" class="pi-meta-item">
              <i class="bx bx-time-five"></i>
              {{ frequencyLabel(item.sync_frequency) }}
            </span>
            <span v-else class="pi-meta-item">
              <i class="bx bx-time-five"></i>
              Automático
            </span>
            <span v-if="item.field_mappings && item.field_mappings.length" class="pi-meta-item">
              <i class="bx bx-transfer"></i>
              {{ item.field_mappings.length }} campos
            </span>
          </div>

          <div class="pi-card-stats">
            <div class="pi-stat">
              <div class="pi-stat-value">{{ item.last_sync_rows || 0 }}</div>
              <div class="pi-stat-label">Registros</div>
            </div>
            <div class="pi-stat">
              <div
                class="pi-stat-value"
                :style="{ color: syncStatusColor(item.last_sync_status) }"
              >
                {{ syncStatusLabel(item.last_sync_status) }}
              </div>
              <div class="pi-stat-label">Último sync</div>
            </div>
            <div class="pi-stat">
              <div class="pi-stat-value">{{ timeAgo(item.last_synced_at) }}</div>
              <div class="pi-stat-label">Atrás</div>
            </div>
          </div>

          <div class="pi-card-action">
            Ver detalhes <i class="bx bx-right-arrow-alt"></i>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="pi-empty">
        <div class="pi-empty-icon" :class="providerDef.iconClass">
          <img
            v-if="providerDef.logo"
            :src="providerDef.logo"
            style="width: 40px; height: 40px; opacity: 0.5;"
          />
          <i v-else :class="providerDef.icon" style="font-size: 40px; opacity: 0.5;"></i>
        </div>
        <p class="pi-empty-text">Nenhuma integração configurada para {{ providerDef.name }}.</p>
        <button class="di-btn di-btn-primary" @click="openNewWizard">
          <i class="bx bx-plus" style="font-size: 16px;"></i>
          Criar Integração
        </button>
      </div>
    </div>

    <!-- Wizard Modal -->
    <WizardModal
      :show="showWizard"
      :form="wizardForm"
      :providers="providers"
      :availableFields="availableFields"
      :isEditing="false"
      :saving="saving"
      @save="saveNewIntegration"
      @close="showWizard = false"
    />
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState } from 'vuex'
import WizardModal from './components/WizardModal.vue'

const PROVIDER_DEFS = {
  google_sheets: {
    key: 'google_sheets',
    name: 'Google Sheets',
    description: 'Sincronize participantes com uma planilha Google Sheets.',
    icon: 'bx bxl-google',
    iconClass: 'sheets',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/a/ae/Google_Sheets_2020_Logo.svg'
  },
  lead_collection: {
    key: 'lead_collection',
    name: 'Coleta de Lead',
    description: 'Envie para uma planilha as coletas de lead feitas no check-in — uma linha por coleta.',
    icon: 'bx bx-user-voice',
    iconClass: 'sheets',
    logo: null
  },
  sympla: {
    key: 'sympla',
    name: 'Sympla',
    description: 'Verifique pagamentos na Sympla e confirme participantes automaticamente.',
    icon: 'mdi mdi-alpha-s-box',
    iconClass: 'sympla',
    logo: null
  },
  whatsapp_positus: {
    key: 'whatsapp_positus',
    name: 'WhatsApp',
    description: 'Use suas proprias credenciais para envio de mensagens via WhatsApp.',
    icon: 'bx bxl-whatsapp',
    iconClass: 'whatsapp',
    logo: null
  },
  eventfy_app: {
    key: 'eventfy_app',
    name: 'App Eventfy',
    description: 'Envie participantes para o App Eventfy quando atingem o status configurado.',
    icon: 'bx bx-mobile-alt',
    iconClass: 'eventfy',
    logo: null
  }
}

export default {
  components: { Layout, PageHeader, WizardModal },
  name: 'provider-integrations',
  page: {
    title: 'Integrações',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  data () {
    const providerKey = this.$route.params.provider
    const def = PROVIDER_DEFS[providerKey] || {
      key: providerKey,
      name: providerKey,
      description: '',
      icon: 'bx bx-plug',
      iconClass: '',
      logo: null
    }
    return {
      loading: true,
      showWizard: false,
      saving: false,
      providerKey,
      providerDef: def,
      wizardForm: this.getEmptyForm(providerKey),
      title: def.name,
      breadcrumbs: [
        { text: 'Configurações', href: '/' },
        { text: 'Integrações', href: '/settings/data-integrations' },
        { text: def.name, active: true }
      ]
    }
  },
  computed: {
    ...mapState('dataIntegrations', ['integrations', 'providers', 'availableFields']),
    providerIntegrations () {
      return this.integrations.filter(i => i.provider === this.providerKey)
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    ...mapActions('dataIntegrations', {
      fetchIntegrations: 'GetIntegrations',
      fetchProviders: 'GetProviders',
      fetchAvailableFields: 'GetAvailableFields',
      saveIntegration: 'SaveIntegration'
    }),
    getEmptyForm (providerKey) {
      return {
        name: '',
        provider: providerKey || 'google_sheets',
        config: {},
        sync_frequency: 'manual',
        filters: { statuses: [], group_ids: [], tag_ids: [] },
        sync_direction: 'push',
        is_active: true,
        field_mappings: []
      }
    },
    async loadData () {
      this.loading = true
      try {
        await this.fetchIntegrations()
      } catch (err) {
        // silent
      }
      this.loading = false
    },
    goToDetail (item) {
      this.$router.push({ name: 'data-integration-form', params: { id: item.id } })
    },
    async openNewWizard () {
      this.wizardForm = this.getEmptyForm(this.providerKey)

      try {
        if (!this.providers.length) await this.fetchProviders()
        if (!this.availableFields.length) await this.fetchAvailableFields()
      } catch (err) {
        // continue
      }

      const provider = this.providers.find(p => p.key === this.providerKey)
      if (provider) {
        if (provider.sync_direction) {
          this.wizardForm.sync_direction = provider.sync_direction
        }
        if (provider.default_frequency) {
          this.wizardForm.sync_frequency = provider.default_frequency
        }
        const cfg = {}
        ;(provider.config_fields || []).forEach(f => {
          cfg[f.key] = f.default !== undefined ? f.default : ''
        })
        this.wizardForm.config = cfg
      }

      this.showWizard = true
    },
    async saveNewIntegration () {
      this.saving = true
      try {
        const res = await this.saveIntegration(this.wizardForm)
        this.showWizard = false
        successMessage('Integração criada com sucesso.', 2500)
        const newId = res.data.data ? res.data.data.id : res.data.id
        if (newId) {
          this.$router.push({ name: 'data-integration-form', params: { id: newId } })
        } else {
          await this.loadData()
        }
      } catch (err) {
        errorMessage('Falha ao criar: ' + (err.message || 'Erro desconhecido'))
      }
      this.saving = false
    },
    frequencyLabel (freq) {
      if (!freq || freq === 'manual') return 'Manual'
      if (freq === 'hourly') return 'A cada 1h'
      if (freq === 'daily') return 'Diária'
      if (freq.startsWith('daily:')) return 'Diária às ' + freq.substring(6)
      const hMatch = freq.match(/^(\d+)h$/)
      if (hMatch) return 'A cada ' + hMatch[1] + 'h'
      const mMatch = freq.match(/^(\d+)min$/)
      if (mMatch) return 'A cada ' + mMatch[1] + ' min'
      return freq
    },
    syncStatusColor (status) {
      return {
        success: '#34c38f',
        error: '#f46a6a',
        partial: '#f1b44c',
        started: '#2563eb'
      }[status] || '#8d92a9'
    },
    syncStatusLabel (status) {
      return {
        success: 'Sucesso',
        error: 'Erro',
        partial: 'Parcial',
        started: 'Rodando...'
      }[status] || '--'
    },
    timeAgo (dateStr) {
      if (!dateStr) return '--'
      const diff = Date.now() - new Date(dateStr).getTime()
      const minutes = Math.floor(diff / 60000)
      if (minutes < 1) return 'Agora'
      if (minutes < 60) return `${minutes} min`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours}h`
      const days = Math.floor(hours / 24)
      return `${days}d`
    }
  }
}
</script>

<style scoped>
/* Header */
.pi-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.pi-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pi-header-icon {
  width: 48px; height: 48px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pi-header-icon.sheets { background: #e8f5e9; color: #0f9d58; }
.pi-header-icon.sympla { background: #eef0ff; color: #4253f0; }
.pi-header-icon.whatsapp { background: #e8f8ef; color: #25d366; }
.pi-header-icon.eventfy { background: #eef0ff; color: #6d4aff; }
.pi-header-title {
  font-size: 18px;
  font-weight: 600;
}
.pi-header-desc {
  font-size: 13px;
  color: #8d92a9;
  margin-top: 2px;
}

/* Grid */
.pi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

/* Card */
.pi-card {
  background: #fff;
  border: 1px solid #e2e5ed;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.pi-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07), 0 1px 4px rgba(0, 0, 0, 0.04);
  transform: translateY(-2px);
  border-color: #d0d4e0;
}
.pi-card.active { border-color: #a7f3d0; }
.pi-card.active::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: #34c38f;
}
.pi-card.inactive { border-color: #e2e5ed; opacity: 0.75; }

/* Card top */
.pi-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.pi-card-name {
  font-size: 15px;
  font-weight: 600;
}

/* Meta */
.pi-card-meta {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}
.pi-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #8d92a9;
}
.pi-meta-item i {
  font-size: 14px;
  opacity: 0.5;
}

/* Stats */
.pi-card-stats {
  display: flex;
  border-top: 1px solid #edf0f5;
  margin: 0 -20px;
  padding: 0;
}
.pi-stat {
  flex: 1;
  padding: 12px 14px;
  text-align: center;
  border-right: 1px solid #edf0f5;
}
.pi-stat:last-child { border-right: none; }
.pi-stat-value {
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
}
.pi-stat-label {
  font-size: 10px;
  color: #8d92a9;
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Action link */
.pi-card-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #2563eb;
  margin: 12px -20px -20px;
  padding: 10px;
  border-top: 1px solid #edf0f5;
  background: #f8f9fb;
}
.pi-card-action i { transition: transform 0.2s; font-size: 16px; }
.pi-card:hover .pi-card-action i { transform: translateX(3px); }

/* Badge */
.int-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.int-badge.active-badge { background: #d4f5e7; color: #0d7a5f; }
.int-badge.inactive-badge {
  background: #f8f9fb;
  color: #8d92a9;
  border: 1px solid #edf0f5;
}

/* Button */
.di-btn {
  padding: 8px 18px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
}
.di-btn-primary { background: #2563eb; color: #fff; }
.di-btn-primary:hover { background: #1d4ed8; }

/* Empty state */
.pi-empty {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border: 1px solid #e2e5ed;
  border-radius: 8px;
}
.pi-empty-icon {
  width: 80px; height: 80px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}
.pi-empty-icon.sheets { background: #e8f5e9; }
.pi-empty-icon.sympla { background: #eef0ff; }
.pi-empty-icon.whatsapp { background: #e8f8ef; }
.pi-empty-icon.eventfy { background: #eef0ff; }
.pi-empty-text {
  font-size: 14px;
  color: #8d92a9;
  margin-bottom: 20px;
}

@media (max-width: 900px) {
  .pi-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .pi-grid { grid-template-columns: 1fr; }
  .pi-header { flex-wrap: wrap; gap: 12px; }
}
</style>
